import { scaleLinear } from 'd3-scale';
import { isEmpty } from 'lodash-es';

import Colors from '@dynatrace/strato-design-tokens/colors';

import { getCanvasColor } from './canvas-color.js';

export type ShapeBuilderPayload = {
  context: CanvasRenderingContext2D;
};
export type CanvasGradientGenerator = (props: ShapeBuilderPayload) => void;

export const DEFAULT_BACKGROUND_DARK = '#000000';

interface CanvasGradientProps {
  height: number;
  color: string;
  domain: number[];
  theme: 'light' | 'dark';
}

/**
 * Given a predefined height, a color and the domain of the data, it creates a linear gradient from top to bottom, bottom to top, or one that goes both directions depending on the domain.
 * @param height - given chart height
 * @param color - given main color of the gradient
 * @param domain - given domain of the chart
 * @param theme - current theme
 * @returns CanvasGradientGenerator
 * @internal
 */
export function createCanvasGradient({
  height,
  color,
  domain,
  theme,
}: CanvasGradientProps): CanvasGradientGenerator {
  return ({ context }) => {
    const gradient = createGradientPattern(
      context,
      height,
      domain,
      color,
      theme,
    );

    fillGradient(context, gradient);
  };
}

/**
 * Gives color to the last path drawn in the CanvasContext, and then closes the path.
 * @param context - given canvas context
 * @param color - given color to apply to gradient
 */
function fillGradient(
  context: CanvasRenderingContext2D,
  color: CanvasGradient,
) {
  context.fillStyle = color;

  context.fill();
  context.closePath();
  context.globalAlpha = 0.8;
  context.save();
}

/**
 * Creates a linear gradient from color to transparent white.
 *
 * Depending on the given domain, it creates a linear gradient from top to bottom, bottom to top, or one that goes both directions.
 *
 * @param context - given canvas context
 * @param height - given mask height
 * @param domain - given data domain
 * @param color - main color of the gradient
 * @param theme - current Theme
 */
function createGradientPattern(
  context: CanvasRenderingContext2D,
  height: number,
  domain: number[],
  color: string,
  theme: 'light' | 'dark',
): CanvasGradient {
  const onlyPositiveValues = domain[0] >= 0;
  const onlyNegativeValues = domain[1] <= 0;
  const strokeColor = getCanvasColor(color);
  const backgroundColor = getCanvasColor(Colors.Theme.Background['10']);
  const backgroundColorRgb = isEmpty(backgroundColor)
    ? DEFAULT_BACKGROUND_DARK
    : backgroundColor;
  const transparentRgba = `${backgroundColorRgb}00`;

  const gradient = context.createLinearGradient(0, 0, 0, height);
  const LightThemeAlpha = 0.4;
  const DarkThemeAlpha = 0.6;
  context.globalAlpha = theme === 'light' ? LightThemeAlpha : DarkThemeAlpha;

  if (onlyPositiveValues) {
    gradient.addColorStop(0, strokeColor);
    gradient.addColorStop(1, transparentRgba);
  } else if (onlyNegativeValues) {
    gradient.addColorStop(0, transparentRgba);
    gradient.addColorStop(1, strokeColor);
  } else {
    const scale = scaleLinear().domain(domain).range([1, 0]);

    const zeroLineStop = scale(0) ?? 0.5;

    gradient.addColorStop(0, strokeColor);
    gradient.addColorStop(zeroLineStop, transparentRgba);
    gradient.addColorStop(1, strokeColor);
  }

  return gradient;
}
